// Global autosave status pill -- the app's "very obvious" save feedback.
//
// Mounted once in App.jsx (not per page) so it keeps reporting a save that is
// still finishing after the page that started it navigated away. Driven
// entirely by the shared SaveQueue snapshot:
//
//   Saving...            while any record's mutation is in flight (held a
//                        minimum ~400ms so fast saves don't strobe)
//   Saved [check]        for ~2s after the last save lands
//   Couldn't save/Retry  persistent until Retry succeeds or the edit changes
//
// Nothing is shown while edits sit in their debounce window -- "dirty" is
// deliberately invisible here.
//
// The visually-hidden aria-live region is ALWAYS mounted (screen readers miss
// regions that appear on demand) and only announces the transitions worth
// hearing: saved and failed, not every keystroke's "Saving...".

import React, { useSyncExternalStore } from 'react';
import Button from 'react-bootstrap/Button';
import { saveQueue } from '../utilities/SaveQueue.mjs';

// A failed save is reported globally and outlives the page that started it,
// so "Couldn't save" on its own leaves the user guessing what is at risk.
// The queue's record keys are `model:id`, so the prefix names the record --
// enough to say "that trip" without threading a label through every editor.
const RECORD_LABELS = {
  item: 'that item',
  task: 'that task',
  trip: 'that trip',
  entry: 'that logbook entry',
  logbook: 'that logbook',
  counter: 'that counter',
  sub: 'that email subscription',
};

function recordLabel(errorKey) {
  if (!errorKey) return null;
  return RECORD_LABELS[String(errorKey).split(':')[0]] || null;
}

const pillContainerStyle = {
  position: 'fixed',
  bottom: '1.25rem',
  left: '50%',
  transform: 'translateX(-50%)',
  // Above react-bootstrap modals (dialog z-index 1055) so the pill stays
  // visible while editing inside one.
  zIndex: 1090,
  // The pill is an overlay, not a control: it must never swallow clicks
  // meant for whatever sits underneath it (the Retry button opts back in).
  pointerEvents: 'none',
};

export const SaveStatusPill = () => {
  const { status, errorMessage, errorKey } = useSyncExternalStore(
    saveQueue.subscribe,
    saveQueue.getSnapshot
  );

  const label = recordLabel(errorKey);
  const errorHeadline = label ? `Couldn't save ${label}` : "Couldn't save";

  // `extractErrorText` falls back to `err.toString()`, which prefixes the
  // constructor name -- fine for a debug tooltip, not for the one line the
  // user reads. Also trim the reason's own trailing punctuation so the
  // announcement doesn't read back "Name required.. A retry button...".
  const reason = errorMessage
    ? String(errorMessage)
        .replace(/^(?:[A-Za-z]*Error|TypeError):\s*/, '')
        .replace(/\s*[.!]+\s*$/, '')
    : '';

  let pill = null;
  if (status === 'saving') {
    pill = (
      <span className="badge rounded-pill text-bg-dark shadow d-inline-flex align-items-center gap-2 px-3 py-2 fs-6 fw-normal">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Saving&hellip;
      </span>
    );
  } else if (status === 'saved') {
    pill = (
      <span className="badge rounded-pill text-bg-dark shadow d-inline-flex align-items-center gap-2 px-3 py-2 fs-6 fw-normal">
        <i className="fa-solid fa-check" aria-hidden="true"></i>
        Saved
      </span>
    );
  } else if (status === 'error') {
    // The reason is rendered, not just a `title` -- hover text is invisible
    // on touch and to screen readers, and this is the one state the user has
    // to act on.
    pill = (
      <span className="badge rounded-pill text-bg-danger shadow d-inline-flex align-items-center gap-2 ps-3 pe-2 py-2 fs-6 fw-normal">
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
        <span className="d-inline-flex flex-column align-items-start text-start">
          <span>{errorHeadline}</span>
          {reason && (
            <span className="small fw-light" style={{ maxWidth: '18rem' }}>
              {reason}
            </span>
          )}
        </span>
        <Button
          size="sm"
          variant="light"
          className="rounded-pill py-0"
          style={{ pointerEvents: 'auto' }}
          onClick={() => saveQueue.retryAll()}
        >
          Retry
        </Button>
      </span>
    );
  }

  const announcement =
    status === 'saved'
      ? 'All changes saved'
      : status === 'error'
        ? `${errorHeadline}.${reason ? ` ${reason}.` : ''} A retry button is available.`
        : '';

  return (
    <>
      <div role="status" aria-live="polite" className="visually-hidden">
        {announcement}
      </div>
      {pill && (
        <div style={pillContainerStyle} data-testid="save-status-pill">
          {pill}
        </div>
      )}
    </>
  );
};

export default SaveStatusPill;
